import React, { useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { MapPin, Phone, Sparkles, Music, CheckCircle2, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { getCityBySlug, CITIES } from "@/data/cities";
import { useSeo, JsonLd } from "@/lib/seo";
import { trackEvent, setLeadContext, cityGroupFromValue } from "@/lib/analytics";

export default function DjMariageVille({ citySlug }) {
  const city = getCityBySlug(citySlug);
  const title = city ? (city.seo_title || `DJ Mariage ${city.name} (${city.cp}) — My Animation`) : "";
  const description = city
    ? city.seo_description || `DJ mariage à ${city.name} : animation musicale, sonorisation professionnelle et mise en lumière. Devis personnalisé sous 24 h. Une solide expérience de l'animation de mariages dans les Hauts-de-France.`
    : "";

  useSeo({
    title,
    description,
    path: city ? `/dj-mariage-${city.slug}` : "",
    keywords: city?.keywords || "",
  });

  // view_local_page — déclenché UNE SEULE FOIS au montage pour chaque ville.
  // Protection anti-StrictMode-double-mount via useRef (dev React 18).
  // Aucun paramètre personnel transmis : service_type, page_type, city (slug), source_page.
  const viewTrackedRef = useRef(null);
  useEffect(() => {
    if (!city) return;
    if (viewTrackedRef.current === city.slug) return;
    viewTrackedRef.current = city.slug;
    trackEvent("view_local_page", {
      service_type: "mariage",
      page_type: "city_mariage",
      city: city.slug,
      city_group: cityGroupFromValue(city.name),
      source_page: `/dj-mariage-${city.slug}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city?.slug]);

  if (!city) return <Navigate to="/mariage" replace />;

  const sourcePage = `/dj-mariage-${city.slug}`;
  const trackCtx = {
    service_type: "mariage",
    page_type: "city_mariage",
    city: city.slug,
    source_page: sourcePage,
  };
  const onClickPhone = () => trackEvent("click_phone", trackCtx);
  const onClickQuote = () => {
    setLeadContext(trackCtx);
    trackEvent("click_quote", trackCtx);
  };
  const onViewService = (target) => {
    trackEvent("view_service", { ...trackCtx, target_page: target });
  };

  const neighborCities = (city.neighbors || [])
    .map((slug) => CITIES.find((c) => c.slug === slug))
    .filter(Boolean);

  return (
    <>
      {/* JSON-LD LocalBusiness — areaServed uniquement, pas d'adresse dans la ville
          (My Animation est basée à Bousbecque ; ce n'est pas une adresse physique à {city.name}) */}
      <JsonLd
        id={`jsonld-city-${city.slug}`}
        data={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `https://myanimation.fr/dj-mariage-${city.slug}`,
          name: "My Animation",
          description: `DJ Mariage professionnel intervenant à ${city.name} et alentours (${city.dept}).`,
          url: `https://myanimation.fr/dj-mariage-${city.slug}`,
          telephone: "+33769224956",
          email: "my-animation@outlook.fr",
          priceRange: "€€€",
          address: {
            "@type": "PostalAddress",
            streetAddress: "66 rue de Linselles",
            addressLocality: "Bousbecque",
            postalCode: "59166",
            addressRegion: "Hauts-de-France",
            addressCountry: "FR",
          },
          areaServed: city.area_served
            ? city.area_served.map((name) => ({ "@type": "City", name }))
            : {
                "@type": "City",
                name: city.name,
                address: {
                  "@type": "PostalAddress",
                  postalCode: city.cp,
                  addressRegion: city.dept,
                  addressCountry: "FR",
                },
              },
          serviceType: [
            "DJ Mariage",
            "Animation événementielle",
            "Sonorisation",
            "Éclairage scénique",
            "Photobooth",
          ],
          sameAs: [
            "https://www.instagram.com/myanimationdj/",
            "https://www.facebook.com/p/Dj-Paps-MY-Animation-61574555792594/",
          ],
        }}
      />

      <PageHero
        eyebrow={`${city.dept} · ${city.cp}`}
        title={
          city.h1 ? (
            <>
              {city.h1.split(" · ")[0]} <span className="italic gold-text">· {city.h1.split(" · ")[1]}</span>
            </>
          ) : (
            <>
              DJ Mariage à <span className="italic gold-text">{city.name}.</span>
            </>
          )
        }
        subtitle={`Animation musicale haut de gamme pour votre mariage à ${city.name}. Une solide expérience de l'animation de mariages dans les Hauts-de-France — devis personnalisé sous 24 h.`}
        img="https://images.pexels.com/photos/1652353/pexels-photo-1652353.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      {/* Intro */}
      <section className="py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <InfoTile icon={MapPin} label="Zone d'intervention" value={city.name} sub={city.intro_sub || "Intervention depuis Bousbecque"} />
          <InfoTile icon={Music} label="Secteur" value={city.cp} sub={`${city.dept} · Hauts-de-France`} />
          <InfoTile icon={Sparkles} label="Devis personnalisé" value="Rapide" sub="Gratuit · sans engagement" />
        </div>

        <div className="card-luxe p-8 md:p-12">
          <p className="eyebrow">Notre expertise locale</p>
          <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
            {city.intro_title || <>Pourquoi nous choisir pour votre mariage à <span className="italic gold-text">{city.name}</span> ?</>}
          </h2>
          <p className="text-white/75 mt-6 leading-relaxed">{city.intro}</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {[
              `Connaissance du tissu local et des salles de ${city.name}`,
              "Sonorisation et éclairage professionnels adaptés à chaque salle",
              "DJ qui lit le public en temps réel (pas une playlist Spotify pré-faite)",
              "Préparation en plusieurs rendez-vous + espace client pour playlists, planning, photobooth",
              "Plan B technique systématique (double système audio)",
              "Devis transparent — pas de surprise, prix TTC",
            ].map((point) => (
              <li key={point} className="flex items-start gap-3 text-sm text-white/75 leading-relaxed">
                <CheckCircle2 size={16} strokeWidth={1.4} className="text-[#DCB457] shrink-0 mt-0.5" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Salles */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <p className="eyebrow">Lieux de réception connus dans le secteur</p>
        <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
          {city.venues_title || <>Lieux connus à <span className="italic gold-text">{city.name}</span> et dans les environs.</>}
        </h2>
        <p className="text-white/65 text-sm mt-3 max-w-2xl">
          Parmi les lieux de réception connus dans le secteur de {city.name} et des communes voisines. Vous avez une autre salle en tête ? Nous nous adaptons à toutes les configurations.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          {city.venues.map((v) => (
            <div key={v.name} className="card-luxe p-6">
              <h3 className="font-serif-luxe text-xl text-[#DCB457]">{v.name}</h3>
              <p className="text-sm text-white/65 mt-2 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
        {city.venues_note && <p className="text-xs text-white/45 italic mt-5" data-testid={`city-venues-note-${city.slug}`}>{city.venues_note}</p>}
      </section>

      <CityCommunes city={city} />
      <CityFaq city={city} />

      {/* Bloc "Votre retour ici" — remplace le témoignage inventé */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[900px] mx-auto">
        <div className="card-luxe p-8 md:p-12 text-center">
          <p className="eyebrow">Retours clients</p>
          <p className="text-white/75 text-base md:text-lg leading-relaxed mt-5">
            Vous avez organisé votre événement à {city.name} avec My Animation ?
            Votre retour pourra être ajouté ici avec votre autorisation.
          </p>
          <Link
            to="/avis"
            data-testid={`city-avis-link-${city.slug}`}
            className="inline-flex items-center gap-2 text-[#DCB457] hover:underline text-sm mt-5"
          >
            Voir tous les avis clients <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Forfaits — bloc unifié "à partir de 1 599 € TTC" */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <div className="card-luxe p-8 md:p-12">
          <p className="eyebrow">Forfaits mariage</p>
          <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
            Des prestations mariage <span className="italic gold-text">à partir de 1 599 € TTC.</span>
          </h2>
          <p className="text-white/75 mt-6 leading-relaxed max-w-3xl">
            Chaque mariage est unique. Le tarif dépend notamment du lieu, des horaires, de la cérémonie,
            du nombre d'invités et des options choisies. Découvrez nos forfaits ou demandez une proposition
            personnalisée pour votre mariage à <span className="text-white">{city.name}</span>.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link
              to="/mariage"
              data-testid={`city-forfaits-link-${city.slug}`}
              onClick={() => onViewService("/mariage")}
              className="btn-gold inline-flex items-center gap-2"
            >
              Voir les forfaits mariage <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <Link
              to="/contact"
              data-testid={`city-forfaits-devis-${city.slug}`}
              onClick={onClickQuote}
              className="btn-ghost-gold inline-flex items-center gap-2"
            >
              Demander un devis personnalisé <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
          </div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-6">
            Tarifs TTC · Proposition sur-mesure selon votre lieu, votre date et vos envies.
          </p>
        </div>
      </section>

      {/* Maillage interne : services + villes voisines + FAQ + avis + contact */}
      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-14 max-w-[1200px] mx-auto border-t border-[#DCB457]/12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <p className="eyebrow">Autres prestations utiles</p>
            <h3 className="font-serif-luxe text-2xl md:text-3xl mt-3">Compléter votre mariage</h3>
            <p className="text-white/65 text-sm mt-3 leading-relaxed">
              La prestation DJ peut être enrichie d'un photobooth pour immortaliser la soirée,
              d'options d'animation ou d'une préparation détaillée sur mesure. Retrouvez
              également nos avis clients et les questions les plus fréquentes.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/mariage" data-testid={`city-link-mariage-${city.slug}`} onClick={() => onViewService("/mariage")} className="btn-ghost-gold text-xs">DJ mariage — la prestation</Link>
              <Link to="/photobooth" data-testid={`city-link-photobooth-${city.slug}`} onClick={() => onViewService("/photobooth")} className="btn-ghost-gold text-xs">Photobooth</Link>
              <Link to="/avis" data-testid={`city-link-avis-${city.slug}`} className="btn-ghost-gold text-xs">Avis clients</Link>
              <Link to="/faq" data-testid={`city-link-faq-${city.slug}`} className="btn-ghost-gold text-xs">FAQ</Link>
              <Link to="/contact" data-testid={`city-link-contact-${city.slug}`} onClick={onClickQuote} className="btn-ghost-gold text-xs">Contact</Link>
            </div>
          </div>

          {neighborCities.length > 0 && (
            <div>
              <p className="eyebrow">Villes voisines</p>
              <h3 className="font-serif-luxe text-2xl md:text-3xl mt-3">Autres villes desservies</h3>
              <p className="text-white/65 text-sm mt-3 leading-relaxed">
                My Animation intervient également à proximité de {city.name}. Consultez nos pages dédiées :
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-5">
                {neighborCities.map((n) => (
                  <Link
                    key={n.slug}
                    to={`/dj-mariage-${n.slug}`}
                    data-testid={`city-neighbor-${city.slug}-${n.slug}`}
                    className="border border-[#DCB457]/20 hover:border-[#DCB457]/60 hover:bg-[#DCB457]/5 px-3 py-2.5 text-sm transition-colors"
                  >
                    <span className="block text-[10px] uppercase tracking-[0.16em] text-white/45">{n.cp}</span>
                    <span className="block text-white group-hover:text-[#DCB457] mt-0.5">{n.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 max-w-[900px] mx-auto">
        <div className="card-luxe p-10 md:p-14 text-center">
          <p className="eyebrow">Devis gratuit sous 24 h</p>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-4">
            Parlons de votre mariage à{" "}<br />
            <span className="italic gold-text">{city.name}.</span>
          </h2>
          <p className="text-white/70 mt-5 max-w-md mx-auto">
            Devis personnalisé, sans engagement. Nous revenons vers vous rapidement avec une proposition adaptée à votre salle, votre date et vos envies.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/contact" data-testid={`city-cta-devis-${city.slug}`} onClick={onClickQuote} className="btn-gold inline-flex items-center gap-2">
              Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <a href="tel:0769224956" data-testid={`city-cta-tel-${city.slug}`} onClick={onClickPhone} className="btn-ghost-gold inline-flex items-center gap-2">
              <Phone size={14} strokeWidth={1.4} /> 07 69 22 49 56
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function CityCommunes({ city }) {
  if (!city.communes?.length) return null;
  return (
    <section className="py-12 md:py-16 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto" data-testid={`city-communes-${city.slug}`}>
      <p className="eyebrow">Communes voisines desservies</p>
      <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">Autour de <span className="italic gold-text">{city.name}.</span></h2>
      <div className="flex flex-wrap gap-2 mt-6">
        {city.communes.map((c) => (
          <span key={c} className="border border-[#DCB457]/25 px-3 py-1.5 text-sm text-white/75">{c}</span>
        ))}
      </div>
    </section>
  );
}

function CityFaq({ city }) {
  if (!city.faq?.length) return null;
  return (
    <section className="py-12 md:py-16 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto" data-testid={`city-faq-${city.slug}`}>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: city.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }}
      />
      <p className="eyebrow">Questions fréquentes</p>
      <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">Les questions des mariés de <span className="italic gold-text">{city.name}.</span></h2>
      <div className="mt-8 space-y-4">
        {city.faq.map((f, i) => (
          <div key={f.q} className="card-luxe p-6" data-testid={`city-faq-${city.slug}-${i}`}>
            <h3 className="font-serif-luxe text-xl text-[#DCB457]">{f.q}</h3>
            <p className="text-sm text-white/70 mt-3 leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function InfoTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="card-luxe p-5 text-center">
      <Icon size={20} strokeWidth={1.3} className="text-[#DCB457] mx-auto" />
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/55 mt-3">{label}</p>
      <p className="font-serif-luxe text-2xl gold-text mt-2">{value}</p>
      <p className="text-[11px] text-white/40 mt-1">{sub}</p>
    </div>
  );
}
